import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Gift, ShoppingCart, Scissors, LogOut, ArrowLeft, Star, ChevronRight, Package, MapPin, Truck, CreditCard, Clock, CheckCircle, XCircle, RotateCcw, Loader2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const formatDate = (d) => d ? new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

const tierColors = { silver: "bg-gray-600/20 text-gray-300", gold: "bg-yellow-600/20 text-yellow-400", platinum: "bg-gray-400/20 text-gray-200", prioritas: "bg-[#E60000]/20 text-[#E60000]" };
const orderStatusColors = { pending: "bg-yellow-900/30 text-yellow-400", processing: "bg-blue-900/30 text-blue-400", shipped: "bg-purple-900/30 text-purple-400", delivered: "bg-green-900/30 text-green-400", cancelled: "bg-red-900/30 text-red-400", expired: "bg-gray-800/30 text-gray-500" };
const paymentStatusColors = { pending: "bg-yellow-900/30 text-yellow-400", paid: "bg-green-900/30 text-green-400", failed: "bg-red-900/30 text-red-400", expired: "bg-gray-800/30 text-gray-500" };

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [midtransReady, setMidtransReady] = useState(false);

  const fetchProfile = useCallback(async () => {
    try { const { data } = await api.get("/customer/profile"); setProfile(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); loadMidtrans(); }, [fetchProfile]);

  const loadMidtrans = async () => {
    try {
      const { data } = await api.get("/payments/client-key");
      if (data.configured && data.client_key) {
        const existing = document.getElementById("midtrans-snap-portal");
        if (existing) existing.remove();
        const script = document.createElement("script");
        script.id = "midtrans-snap-portal";
        script.src = data.snap_url || "https://app.midtrans.com/snap/snap.js";
        script.setAttribute("data-client-key", data.client_key);
        script.async = true;
        script.onload = () => setMidtransReady(true);
        document.head.appendChild(script);
      }
    } catch {}
  };

  const openOrder = async (order) => {
    setSelectedOrder(order);
    setOrderDetail(null);
    setTracking(null);
    try {
      const { data } = await api.get(`/orders/${order.id}`);
      setOrderDetail(data);
      if (data.awb_number) fetchTracking(order.id);
    } catch { toast.error("Gagal memuat detail pesanan"); }
  };

  const fetchTracking = async (oid) => {
    setTrackingLoading(true);
    try { const { data } = await api.get(`/orders/${oid}/tracking`); setTracking(data); } catch {} finally { setTrackingLoading(false); }
  };

  const retryPayment = async () => {
    if (!orderDetail) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderDetail.id}/retry-payment`);
      if (data.token && midtransReady && window.snap) {
        window.snap.pay(data.token, {
          onSuccess: () => { toast.success("Pembayaran berhasil!"); setSelectedOrder(null); fetchProfile(); },
          onPending: () => { toast.info("Menunggu pembayaran"); setSelectedOrder(null); fetchProfile(); },
          onError: () => { toast.error("Pembayaran gagal"); },
          onClose: () => { toast.info("Popup ditutup"); },
        });
      } else if (data.redirect_url) {
        window.open(data.redirect_url, "_blank");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal memproses pembayaran";
      toast.error(msg);
      if (msg.includes("expired")) { fetchProfile(); setSelectedOrder(null); }
    } finally { setPaying(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#050505]"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return <div className="flex items-center justify-center h-screen bg-[#050505] text-gray-400">Gagal memuat profil</div>;

  const visitProgress = (profile.visit_count % 5) / 5 * 100;
  const activeCoupons = profile.coupons?.filter(c => !c.is_claimed) || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed w-full z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#262626]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Link to="/"><img src={LOGO} alt="FI" className="w-8 h-8 rounded-full" /></Link>
            <span className="font-heading text-sm font-bold uppercase tracking-wider">Portal Member</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/toko" className="text-sm text-gray-400 hover:text-white flex items-center gap-1"><ShoppingCart size={14} /> Toko</Link>
            <Link to="/" className="text-sm text-gray-400 hover:text-white flex items-center gap-1" data-testid="portal-home"><ArrowLeft size={14} /> Beranda</Link>
            <button onClick={async () => { await logout(); navigate("/login"); }} className="text-sm text-gray-400 hover:text-[#E60000] flex items-center gap-1" data-testid="portal-logout"><LogOut size={14} /> Keluar</button>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-16 px-6 md:px-12 max-w-5xl mx-auto">
        {/* Profile Card */}
        <div className="bg-[#0F0F0F] border border-[#262626] p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="profile-card">
          <div className="w-16 h-16 bg-[#E60000]/10 border border-[#E60000]/30 flex items-center justify-center shrink-0"><User size={28} className="text-[#E60000]" /></div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">{profile.name}</h1>
            <p className="text-gray-400 text-sm">{profile.email} | {profile.phone}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={tierColors[profile.user_type] || ""}>{profile.user_type?.toUpperCase()}</Badge>
              <span className="text-xs text-gray-500">Member sejak {new Date(profile.created_at).toLocaleDateString("id-ID")}</span>
            </div>
          </div>
          <Link to="/toko"><Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-9 font-bold uppercase text-xs"><ShoppingCart size={14} className="mr-2" /> Belanja</Button></Link>
        </div>

        {/* Loyalty Card */}
        <div className="bg-[#0F0F0F] border border-[#262626] p-6 mb-6" data-testid="loyalty-card">
          <div className="flex items-center gap-2 mb-4"><Gift size={20} className="text-[#E60000]" /><h2 className="font-heading text-lg font-bold uppercase tracking-tight">Program Loyalitas</h2></div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-[#050505] border border-[#262626] p-4 text-center"><p className="font-heading text-2xl font-bold text-white">{profile.visit_count}</p><p className="text-xs text-gray-500">Total Kunjungan</p></div>
            <div className="bg-[#050505] border border-[#262626] p-4 text-center"><p className="font-heading text-2xl font-bold text-[#E60000]">{profile.visits_until_next}</p><p className="text-xs text-gray-500">Lagi untuk Kupon</p></div>
            <div className="bg-[#050505] border border-[#262626] p-4 text-center"><p className="font-heading text-2xl font-bold text-green-400">{activeCoupons.length}</p><p className="text-xs text-gray-500">Kupon Aktif</p></div>
          </div>
          <div className="relative h-3 bg-[#262626] overflow-hidden mb-2"><div className="absolute inset-y-0 left-0 bg-[#E60000] transition-all duration-500" style={{ width: `${visitProgress}%` }} /></div>
          <p className="text-xs text-gray-500">{profile.visit_count % 5}/5 kunjungan</p>
          {activeCoupons.length > 0 && <div className="mt-4 flex gap-2 flex-wrap">{activeCoupons.map(c => (<div key={c.id} className="border border-[#E60000]/30 bg-[#E60000]/5 px-3 py-2 flex items-center gap-2"><Star size={12} className="text-[#E60000]" /><span className="font-mono text-xs text-[#E60000] font-bold">{c.coupon_code}</span></div>))}</div>}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="w-full" data-testid="portal-tabs">
          <TabsList className="w-full bg-[#0F0F0F] border border-[#262626] rounded-none h-11">
            <TabsTrigger value="orders" className="flex-1 rounded-none font-bold uppercase text-xs data-[state=active]:bg-[#E60000] data-[state=active]:text-white" data-testid="tab-orders">Pesanan Online</TabsTrigger>
            <TabsTrigger value="transactions" className="flex-1 rounded-none font-bold uppercase text-xs data-[state=active]:bg-[#E60000] data-[state=active]:text-white" data-testid="tab-transactions">Riwayat POS</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="orders-list">
              {(!profile.orders || profile.orders.length === 0) ? (
                <div className="p-8 text-center text-gray-500">Belum ada pesanan<br /><Link to="/toko" className="text-[#E60000] text-sm mt-2 inline-block">Mulai Belanja</Link></div>
              ) : (
                profile.orders.map(o => (
                  <button key={o.id} onClick={() => openOrder(o)} className="w-full text-left p-4 border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors flex items-center justify-between" data-testid={`order-card-${o.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-[#E60000] font-bold">{o.order_code}</span>
                        <Badge className={paymentStatusColors[o.payment_status] || ""}>{o.payment_status}</Badge>
                        <Badge className={orderStatusColors[o.order_status] || ""}>{o.order_status}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-400">{o.items?.length || 0} item</span>
                        <span className="text-white font-semibold">{formatPrice(o.total)}</span>
                        <span className="text-gray-500 text-xs">{formatDate(o.created_at)}</span>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-500" />
                  </button>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <div className="bg-[#0F0F0F] border border-[#262626]" data-testid="transactions-list">
              {(!profile.transactions || profile.transactions.length === 0) ? (
                <div className="p-8 text-center text-gray-500">Belum ada riwayat kunjungan</div>
              ) : (
                profile.transactions.map(tx => (
                  <div key={tx.id} className="p-4 border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors">
                    <div className="flex items-center justify-between mb-2"><span className="font-mono text-xs text-[#E60000]">{tx.transaction_code}</span><span className="text-white font-semibold">{formatPrice(tx.total)}</span></div>
                    <div className="text-xs text-gray-400">{tx.items?.map(i => i.name).join(", ")}</div>
                    <p className="text-[10px] text-gray-500 mt-1">{formatDate(tx.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg rounded-none max-h-[85vh] overflow-y-auto p-0">
          {orderDetail ? (
            <>
              <div className="p-5 border-b border-[#262626]">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2"><Package size={18} className="text-[#E60000]" /> Detail Pesanan</DialogTitle>
                  <DialogDescription className="text-gray-400 text-sm">{orderDetail.order_code}</DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 mt-3">
                  <Badge className={paymentStatusColors[orderDetail.payment_status] || ""}>{orderDetail.payment_status}</Badge>
                  <Badge className={orderStatusColors[orderDetail.order_status] || ""}>{orderDetail.order_status}</Badge>
                </div>
              </div>

              {/* Items */}
              <div className="p-5 border-b border-[#262626]">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Item Pesanan</p>
                {orderDetail.items?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1.5 text-sm"><span className="text-gray-300">{item.name} x{item.qty}</span><span className="text-white">{formatPrice(item.price * item.qty)}</span></div>
                ))}
                <div className="border-t border-[#262626] mt-2 pt-2 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Subtotal</span><span className="text-white">{formatPrice(orderDetail.subtotal)}</span></div>
                  {orderDetail.shipping_cost > 0 && <div className="flex justify-between text-sm"><span className="text-gray-400">Ongkir ({orderDetail.shipping_courier})</span><span className="text-white">{formatPrice(orderDetail.shipping_cost)}</span></div>}
                  <div className="flex justify-between font-bold text-base pt-1 border-t border-[#262626]"><span className="font-heading uppercase">Total</span><span className="text-[#E60000] font-heading">{formatPrice(orderDetail.total)}</span></div>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="p-5 border-b border-[#262626]">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1"><MapPin size={12} /> Informasi Pengiriman</p>
                <p className="text-white text-sm">{orderDetail.customer_name}</p>
                <p className="text-gray-400 text-xs mt-1">{orderDetail.customer_phone}</p>
                <p className="text-gray-400 text-xs mt-1">{orderDetail.shipping_address}</p>
                {orderDetail.shipping_courier && <p className="text-gray-400 text-xs mt-2">Ekspedisi: <span className="text-white">{orderDetail.shipping_courier}</span></p>}
              </div>

              {/* AWB / Tracking */}
              {(orderDetail.awb_number || orderDetail.order_status === "shipped" || orderDetail.order_status === "delivered") && (
                <div className="p-5 border-b border-[#262626]">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1"><Truck size={12} /> Tracking Pengiriman</p>
                  {orderDetail.awb_number ? (
                    <>
                      <div className="bg-[#050505] border border-[#262626] p-3 mb-3">
                        <p className="text-xs text-gray-500">No. Resi (AWB)</p>
                        <p className="text-white font-mono text-sm font-bold">{orderDetail.awb_number}</p>
                        <p className="text-xs text-gray-400 mt-1">Kurir: {orderDetail.tracking_courier || orderDetail.shipping_courier}</p>
                      </div>
                      {trackingLoading ? (
                        <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={14} className="animate-spin" /> Memuat tracking...</div>
                      ) : tracking?.data ? (
                        <div className="space-y-2 max-h-48 overflow-auto">
                          {(Array.isArray(tracking.data) ? tracking.data : [tracking.data]).map((t, i) => (
                            <div key={i} className="bg-[#050505] border border-[#262626] p-2">
                              {t.summary && <p className="text-white text-xs font-semibold">{t.summary.status || t.summary.description}</p>}
                              {t.history && Array.isArray(t.history) && t.history.slice(0, 5).map((h, j) => (
                                <div key={j} className="mt-1 text-[10px] text-gray-400 border-l-2 border-[#262626] pl-2 ml-1">{h.date} - {h.description}</div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : tracking?.message ? (
                        <p className="text-gray-500 text-xs">{tracking.message}</p>
                      ) : (
                        <Button onClick={() => fetchTracking(orderDetail.id)} className="bg-[#262626] hover:bg-[#333] text-white rounded-none h-8 text-xs" data-testid="refresh-tracking"><RotateCcw size={12} className="mr-1" /> Cek Tracking</Button>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 text-xs">Nomor resi belum tersedia. Admin akan mengisi saat pesanan dikirim.</p>
                  )}
                </div>
              )}

              {/* Payment Action */}
              <div className="p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1"><CreditCard size={12} /> Pembayaran</p>
                {orderDetail.payment_status === "paid" && (
                  <div className="bg-green-900/10 border border-green-800 p-3 flex items-center gap-2"><CheckCircle size={16} className="text-green-400" /><span className="text-green-400 text-sm font-semibold">Pembayaran telah dikonfirmasi</span></div>
                )}
                {orderDetail.payment_status === "pending" && (
                  <div className="space-y-3">
                    <div className="bg-yellow-900/10 border border-yellow-800 p-3 flex items-center gap-2"><Clock size={16} className="text-yellow-400" /><span className="text-yellow-400 text-sm">Menunggu pembayaran QRIS</span></div>
                    <Button onClick={retryPayment} disabled={paying} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-11" data-testid="retry-pay-btn">
                      {paying ? <Loader2 size={16} className="animate-spin mr-2" /> : <CreditCard size={16} className="mr-2" />}
                      {paying ? "Memproses..." : "Bayar Sekarang"}
                    </Button>
                  </div>
                )}
                {orderDetail.payment_status === "expired" && (
                  <div className="bg-gray-800/20 border border-gray-700 p-3 flex items-center gap-2"><XCircle size={16} className="text-gray-500" /><span className="text-gray-500 text-sm">Pembayaran telah expired. Pesanan tidak dapat diproses.</span></div>
                )}
                {orderDetail.payment_status === "failed" && (
                  <div className="bg-red-900/10 border border-red-800 p-3 flex items-center gap-2"><XCircle size={16} className="text-red-400" /><span className="text-red-400 text-sm">Pembayaran gagal</span></div>
                )}
              </div>
            </>
          ) : (
            <div className="p-8 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-[#E60000]" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
